import type { CrmMatchedAccount } from "app/pages/sales-intelligence/domain/crm-integration/types/account";
import type { CrmExportResultDto } from "app/pages/sales-intelligence/domain/crm-integration/types/export";
import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import type { LeadsType } from "app/pages/sales-intelligence/domain/leads/types/lead.ts";

export type AccountExportPreview = {
    domain: string;
    companyName: string;
    matchedCompanies: readonly CrmMatchedAccount[];
    /**
     * Per-row user selection.
     * - `NEW_ACCOUNT_VALUE` -> create a new CRM account
     * - existing CRM account id -> update that account
     */
    accountId?: string;
    /** Label of an account picked via CRM search (not present in `matchedCompanies`). */
    searchedCompanyLabel?: string;
    /**
     * User-edited name for a new CRM account (only meaningful when creating a new account).
     * `undefined` -> fall back to the default name (`companyName || domain`).
     */
    newAccountName?: string;
};

export type AccountsExportPhase =
    | { type: "idle" }
    | { type: "matchingAccounts"; integration: CrmIntegrationProvider }
    | { type: "previewingAccounts"; integration: CrmIntegrationProvider }
    | { type: "exportingAccounts"; integration: CrmIntegrationProvider };

export type AccountsExportState = {
    phase: AccountsExportPhase;
    integration: CrmIntegrationProvider | null;
    leadsType: LeadsType | null;
    listId?: string;
    countries: readonly number[];
    preview: readonly AccountExportPreview[];
    isFetchingPreview: boolean;
};

export const BULK_CHOOSE = "choose";

export type AccountsExportBulkAction = "create-all-new" | "create-or-update";

export type AccountsExportBulkSelection = typeof BULK_CHOOSE | AccountsExportBulkAction;

/** API DTOs */
export type AccountsExportPreviewRequestDto = {
    domains: readonly string[];
    countries: readonly number[];
    listId?: string;
};

export type AccountsExportPreviewResponseItemDto = {
    domain: string;
    companyName: string;
    matchedCompanies: readonly CrmMatchedAccount[];
};

export type AccountsExportPreviewResponseDto = readonly AccountsExportPreviewResponseItemDto[];

export type AccountExportSelectionDto = {
    domain: string;
    accountId: string;
    /**
     * Custom name for a new account. Only sent when `accountId` is empty (create new).
     * When omitted, the backend applies a default name.
     */
    companyName?: string;
};

export type AccountsExportRequestDto = {
    countries: readonly number[];
    listId?: string;
    accounts: readonly AccountExportSelectionDto[];
};
